import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '../lib/useErrorMessage';
import { useAdminBrands, useAssignBrand, useCompanies, useDeleteBrand } from './api';

/**
 * Brand a database e associazione con la company.
 *
 * L'associazione vive solo qui: il JSON non contiene alcun concetto di company
 * e la chiave di collegamento è lo slug del brand.
 */
export function BrandsPanel(): ReactNode {
  const { t } = useTranslation();
  const brands = useAdminBrands();
  const companies = useCompanies();
  const assign = useAssignBrand();
  const remove = useDeleteBrand();
  const describeError = useErrorMessage();

  // Conferma in due tempi: la cancellazione porta via anche il payload.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  return (
    <section className="admin-card">
      <header className="admin-card-head">
        <h2>{t('admin.brands.title')}</h2>
        <p>{t('admin.brands.subtitle')}</p>
      </header>

      {remove.isError ? <p className="admin-alert error">{describeError(remove.error)}</p> : null}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('admin.brands.brand')}</th>
            <th>{t('admin.brands.sector')}</th>
            <th>{t('admin.brands.company')}</th>
            <th>{t('admin.brands.snapshots')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(brands.data ?? []).map((brand) => (
            <tr key={brand.id}>
              <td>
                <div className="admin-strong">{brand.name}</div>
                <code className="admin-mono">{brand.slug}</code>
              </td>
              <td>{brand.sector}</td>
              <td>
                <select
                  value={brand.companyId ?? ''}
                  disabled={assign.isPending}
                  onChange={(e) =>
                    assign.mutate({ brandId: brand.id, companyId: e.target.value || null })
                  }
                >
                  <option value="">{t('admin.brands.unassigned')}</option>
                  {(companies.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </td>
              <td className="admin-mono">{brand.historyCount}</td>
              <td className="admin-right">
                {confirmDelete === brand.id ? (
                  <>
                    <button
                      type="button"
                      className="admin-btn danger"
                      onClick={() =>
                        remove.mutate(brand.id, { onSettled: () => setConfirmDelete(null) })
                      }
                    >
                      {t('admin.brands.confirmDelete')}
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => setConfirmDelete(null)}
                    >
                      {t('admin.brands.cancel')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={() => setConfirmDelete(brand.id)}
                  >
                    {t('admin.brands.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {brands.data?.length === 0 ? (
            <tr>
              <td colSpan={5} className="admin-empty">
                {t('admin.brands.empty')}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
